import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ClipboardCheck, CheckCircle, Shield, AlertTriangle, Car, Wrench, FileText, Eye, ExternalLink, Camera } from 'lucide-react';
import axios from 'axios';
import { trackExternalClick } from '@/utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Screan.co.za official links and prices (updated December 2025)
const SCREAN_LINKS = {
  main: 'https://screan.co.za',
  carScrean: 'https://screan.co.za/products/car-screaning',
  dealerScrean: 'https://screan.co.za/products/dealer-car-screan-plus-independent-service-for-resellers',
  howItWorks: 'https://screan.co.za/pages/how-screan-works'
};

const Inspector = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const car = location.state?.car;

  const handleBookOnScrean = async (url, packageName) => {
    // Track the click in both systems
    try {
      await Promise.all([
        axios.post(`${API}/track/click`, {
          action_type: 'screan_booking',
          source: 'Screan',
          referral_url: url,
          car_id: car?.id
        }),
        trackExternalClick(url, packageName, '/inspector')
      ]);
    } catch (e) {
      console.error('Failed to track click:', e);
    }
    
    // Open Screan booking page
    window.open(url, '_blank');
  };

  // Official Screan.co.za prices (updated December 2025)
  const inspectionTypes = [
    {
      name: 'Car Screan',
      price: 'R1,295',
      priceValue: 1295,
      duration: '~60 min',
      points: '60+ points',
      screanUrl: SCREAN_LINKS.carScrean,
      popular: true,
      features: [
        '60-point vehicle checklist',
        'Test drive assessment',
        'Service history review',
        '360° high-res images',
        'Detailed digital report',
        'Report within 2 hours'
      ]
    },
    {
      name: 'Car Screan PLUS',
      price: 'R1,495',
      priceValue: 1495,
      duration: '~90 min',
      points: '80+ points',
      screanUrl: SCREAN_LINKS.carScrean,
      popular: false,
      features: [
        'Everything in Car Screan',
        'Extended inspection',
        'OBD diagnostic scan',
        'Paint thickness check',
        'Comprehensive report',
        'Priority support'
      ]
    },
    {
      name: 'Dealer Car Screan',
      price: 'Contact for pricing',
      priceValue: 0,
      duration: '~60 min',
      points: '60+ points',
      screanUrl: SCREAN_LINKS.dealerScrean,
      features: [
        'Dealer-focused report',
        'Trade-in assessment',
        'Wholesale value estimate',
        'Bulk booking discounts',
        'Quick turnaround',
        'Volume pricing available'
      ]
    }
  ];

  const checkPoints = [
    { icon: Car, title: 'Body & Frame', desc: 'Rust, accident damage, panel alignment, paint condition' },
    { icon: Wrench, title: 'Mechanical', desc: 'Engine, transmission, suspension, brakes, steering' },
    { icon: Eye, title: 'Interior', desc: 'Seats, dashboard, electronics, AC, lights, switches' },
    { icon: Camera, title: '360° Images', desc: 'High-resolution photos of every angle and detail' },
    { icon: FileText, title: 'Documentation', desc: 'Service history, registration, licence disc, finance check' }
  ];

  const stats = [
    { value: '40%', label: 'of used cars have hidden defects' },
    { value: 'R25,000', label: 'average repair cost for missed issues' },
    { value: '1 in 5', label: 'vehicles have odometer fraud' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <section className="bg-gradient-to-br from-green-600 to-green-800 text-white py-16">
        <div className="container">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-3 py-1 bg-white/20 rounded-full text-sm">Inspections by Screan.co.za</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Independent Vehicle Inspections</h1>
            <p className="text-xl text-green-100 mb-6">
              Don't buy a lemon! Get a professional inspection before you sign. Screan's certified inspectors check 60+ points with detailed reports.
            </p>
            <button
              onClick={() => handleBookOnScrean(SCREAN_LINKS.carScrean, 'Car Screan')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-green-700 rounded-lg font-semibold hover:bg-green-50 transition"
            >
              Book Inspection on Screan.co.za
              <ExternalLink className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Warning Stats */}
      <section className="bg-amber-50 border-b border-amber-200 py-6">
        <div className="container">
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-amber-600">{stat.value}</div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="container py-12">
        {/* If coming from a specific car */}
        {car && (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-6 mb-8">
            <h3 className="font-bold text-gray-900 mb-2">Inspecting this vehicle?</h3>
            <p className="text-gray-700 mb-4">{car.title}</p>
            <p className="text-sm text-gray-500 mb-4">From: {car.source}</p>
            <button
              onClick={() => handleBookOnScrean(SCREAN_LINKS.carScrean, 'Car Screan')}
              className="px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition flex items-center gap-2"
            >
              Book Inspection for This Car
              <ExternalLink className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* What We Check */}
        <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">What Screan Inspects</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {checkPoints.map((point, idx) => (
              <div key={idx} className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <point.icon className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{point.title}</h3>
                  <p className="text-sm text-gray-600">{point.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Official Screan Packages */}
        <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Screan Inspection Packages</h2>
            <span className="text-sm text-gray-500">Official Screan.co.za prices</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {inspectionTypes.map((type, idx) => (
              <div key={idx} className={`border-2 rounded-xl p-5 relative ${type.popular ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                {type.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-green-500 text-white text-xs font-medium rounded-full">
                    Most Popular
                  </div>
                )}
                <h3 className="font-bold text-gray-900 text-lg mb-1">{type.name}</h3>
                <div className="text-2xl font-bold text-green-600 mb-2">{type.price}</div>
                <div className="text-sm text-gray-500 mb-4">
                  {type.duration} • {type.points}
                </div>
                <ul className="space-y-2 mb-4">
                  {type.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleBookOnScrean(type.screanUrl, type.name)}
                  className="w-full py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition flex items-center justify-center gap-1"
                >
                  Book on Screan.co.za
                  <ExternalLink className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
          
          <div className="mt-6 p-4 bg-gray-50 rounded-xl">
            <p className="text-sm text-gray-600 text-center">
              Prices as listed on <a href={SCREAN_LINKS.main} target="_blank" rel="noopener noreferrer" className="text-green-600 font-medium hover:underline">Screan.co.za</a>. 
              Additional travel fees may apply for outlying areas. Full refund if inspector cannot access vehicle.
            </p>
          </div>
        </div>

        {/* Why Inspect */}
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Why You NEED an Inspection</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Private sellers and some dealers may hide vehicle problems</li>
                <li>• Cosmetic repairs can mask serious accident damage</li>
                <li>• Odometer tampering is more common than you think</li>
                <li>• Finance owing or stolen vehicles won't transfer to your name</li>
                <li>• A R1,295 inspection could save you R50,000+ in repairs</li>
              </ul>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <button
            onClick={() => handleBookOnScrean(SCREAN_LINKS.carScrean, 'Car Screan')}
            className="px-8 py-4 bg-green-600 text-white rounded-xl font-bold text-lg hover:bg-green-700 transition flex items-center gap-2 mx-auto"
          >
            Book Your Inspection Now
            <ExternalLink className="w-5 h-5" />
          </button>
          <p className="text-sm text-gray-500 mt-4">
            You'll be redirected to Screan.co.za to complete your booking
          </p>
        </div>
      </div>
    </div>
  );
};

export default Inspector;
